import type { IGModalBridgeProps } from '~/presentation/ui';
import type { ModalProps } from '@shopify/polaris';

import { BlockStack, Box, Button, Modal } from '@shopify/polaris';
import { useWindowSize } from '~/presentation/common/hooks';
import { GModalBridge } from '~/presentation/ui';

export interface GModalProps extends Omit<ModalProps, 'title'> {
  title: string | React.ReactNode;
  id?: string;
  /**
   * @default 'large'
   * When the `isUseAppBridgeModal` prop is set to true, the `size` value is internally mapped to a corresponding `variant`:
   * - 'small' becomes 'base'
   * - 'large' remains 'large'
   * - 'fullScreen' becomes 'max'
   */
  size?: ModalProps['size'];
  primaryAction?: ModalProps['primaryAction'] & {
    variant?: 'primary' | 'breadcrumb' | null;
  };
  isUseAppBridgeModal?: boolean;
  children: React.ReactNode;
}

export const GModal = ({
  children,
  isUseAppBridgeModal = false,
  id,
  primaryAction,
  secondaryActions,
  ...props
}: GModalProps) => {
  const { isMobileView } = useWindowSize();

  const resolvedSecondaryActions = secondaryActions ?? [];
  const showMobileFooter = isMobileView && (primaryAction || resolvedSecondaryActions.length > 0);

  if (!isUseAppBridgeModal) {
    return (
      <Modal
        {...props}
        size={isMobileView ? 'small' : props.size}
        primaryAction={showMobileFooter ? undefined : primaryAction}
        secondaryActions={showMobileFooter ? undefined : secondaryActions}
      >
        {children}
        {showMobileFooter && (
          <Box padding="400" borderBlockStartWidth="025" borderColor="border-secondary">
            <BlockStack gap="200">
              {primaryAction && (
                <Button
                  loading={primaryAction?.loading}
                  disabled={primaryAction?.disabled}
                  accessibilityLabel={primaryAction?.accessibilityLabel}
                  onClick={primaryAction?.onAction}
                  variant="primary"
                  fullWidth
                >
                  {primaryAction?.content ?? 'Save'}
                </Button>
              )}
              {resolvedSecondaryActions.map((action) => (
                <Button
                  key={action.content}
                  loading={action.loading}
                  disabled={action.disabled}
                  accessibilityLabel={action.accessibilityLabel}
                  fullWidth
                  onClick={action.onAction}
                >
                  {action.content}
                </Button>
              ))}
            </BlockStack>
          </Box>
        )}
      </Modal>
    );
  }

  const { open, size, title, noScroll, onClose } = props;

  const variant = getModalBridgeVariant();

  return (
    <GModalBridge
      id={id}
      variant={variant}
      title={typeof title === 'string' ? title : undefined}
      open={!!open}
      isHideScrollbar={noScroll}
      onHide={() => onClose?.()}
      primaryAction={{
        variant: primaryAction?.variant ?? 'primary',
        content: primaryAction?.content ?? '',
        disabled: primaryAction?.disabled,
        loading: primaryAction?.loading,
        onAction: primaryAction?.onAction || (() => {}),
      }}
      secondaryAction={{
        content: secondaryActions?.[0]?.content ?? '',
        loading: secondaryActions?.[0]?.loading,
        onAction: secondaryActions?.[0]?.onAction || (() => {}),
      }}
    >
      {children}
    </GModalBridge>
  );

  function getModalBridgeVariant(): IGModalBridgeProps['variant'] {
    switch (size) {
      case 'small':
        return 'base';
      case 'fullScreen':
        return 'max';
      default:
        return 'large';
    }
  }
};
